"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, ArrowRight, MapPin, CircleCheck as CheckCircle2, Phone, Mail, Sparkles, Search } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const faqData = [{
  id: "faq-1",
  question: "Why is Local SEO crucial for businesses based in Northampton?",
  answer: "Northampton is a competitive regional hub across Northamptonshire, with strong local search demand across professional services, industrial estates (like Brackmills and Grange Park), healthcare, trades, and retail. When potential clients search for services in Northampton, Google's Map Pack and top organic results capture over 60% of all local clicks. I engineer precise Local SEO campaigns so your business dominates those prime positions, turning local searchers into direct leads.",
  category: "Local Market"
}, {
  id: "faq-2",
  question: "Will I work directly with you, Yassine, or an account manager?",
  answer: "You work directly with me — Yassine Essaadi. I am an independent freelance SEO consultant, not an agency with revolving junior account managers. From the initial audit and keyword mapping to technical optimization and local link acquisition, every strategy and deliverable is personally crafted and executed by me.",
  category: "Consultancy Model"
}, {
  id: "faq-3",
  question: "How long does it take to rank on page 1 of Google in Northampton?",
  answer: "For most targeted Northampton keywords, visible rank improvements appear within 6 to 12 weeks following technical fixes and Google Business Profile optimization. Competitive sectors or broader Northamptonshire coverage typically achieve sustainable top 3 positions within 4 to 6 months of systematic local authority building.",
  category: "Timelines & Results"
}, {
  id: "faq-4",
  question: "How do you optimize my Google Business Profile for Northampton searches?",
  answer: "I conduct a deep audit of your Google Business Profile (GBP), fixing primary and secondary categories, optimizing geo-targeted service descriptions, establishing local citation consistency across UK business directories, managing review strategies, and adding geotagged photos. This approach drives your listing straight into the high-converting Northampton Local 3-Pack.",
  category: "Google Business Profile"
}, {
  id: "faq-5",
  question: "My business serves Northampton plus nearby towns like Towcester & Kettering. Can we target multiple areas?",
  answer: "Yes. I build multi-location landing page structures and service-area optimizations that rank your business naturally across Northampton, Wellingborough, Kettering, Towcester, and Daventry without triggering Google duplicate content penalties or doorway page filters.",
  category: "Service Areas"
}, {
  id: "faq-6",
  question: "How does your freelance Local SEO service differ from a Northampton agency?",
  answer: "Agencies often charge high overhead markups while assigning your account to entry-level staff using rigid templates. As an independent consultant, I provide agile, senior-level expertise, direct communication via phone or email, bespoke technical strategies, and transparent reporting with zero agency fluff.",
  category: "Consultancy Model"
}, {
  id: "faq-7",
  question: "Do you require long-term contracts for local SEO consulting?",
  answer: "No long-term lock-in contracts. I work on straightforward monthly retainers or defined project scopes. My focus is on delivering clear ROI, transparent deliverables, and measurable rank gains that earn your continued partnership naturally month after month.",
  category: "Pricing & Terms"
}, {
  id: "faq-8",
  question: "Can Local SEO help B2B companies in Northampton industrial parks?",
  answer: "Absolutely. B2B buyers regularly search for local suppliers, logistics partners, engineering firms, and IT providers operating in Brackmills, Swan Valley, and Moulton Park. I optimize B2B search visibility for high-intent corporate terms, attracting decision-makers seeking local UK suppliers.",
  category: "B2B Local SEO"
}, {
  id: "faq-9",
  question: "How do AI Search updates like Google AI Overviews affect my Northampton rankings?",
  answer: "Modern search combines traditional organic results, local map packs, and AI-driven answers. I optimize your brand's digital entity using structured schema markup, authoritative local citations, and comprehensive FAQ markup so your business is cited directly in Google AI Overviews for Northampton queries.",
  category: "AI & Future SEO"
}, {
  id: "faq-10",
  question: "What metrics do you report on each month?",
  answer: "I report on real business metrics: rank movements for targeted Northampton keywords, Google Map Pack impressions and direct directions/calls, organic traffic increases, direct form submissions, and phone call conversions. No vanity metrics or meaningless jargon.",
  category: "Reporting & KPIs"
}, {
  id: "faq-11",
  question: "How do we get started with a Northampton local SEO audit?",
  answer: "We begin with a free 30-minute consultation where I review your current Northampton search visibility, analyze your top local competitors, and outline a clear action plan. Simply pick a date on my calendar or drop me a message.",
  category: "Getting Started"
}];
const categoryFilters = ["All Questions", "Local Market", "Consultancy Model", "Google Business Profile", "Timelines & Results"];
export default function Northamptonlocalseofaq() {
  const [selectedCategory, setSelectedCategory] = useState("All Questions");
  const [searchQuery, setSearchQuery] = useState("");
  const shouldReduceMotion = useReducedMotion();
  const filteredFaqs = faqData.filter(item => {
    const matchesCategory = selectedCategory === "All Questions" || item.category === selectedCategory;
    const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) || item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  return <section id="northamptonlocalseofaq" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="aria-hidden:hidden pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-gradient-to-b from-secondary/30 via-background/50 to-transparent blur-3xl -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-medium tracking-wide uppercase">
            <MapPin className="size-3 text-primary mr-1" />
            Northamptonshire SEO Insights
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground text-balance">
            Northampton Local SEO Questions Answered
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Clear, honest answers about growing your search visibility across Northampton and surrounding UK areas. No jargon, no agency fluff — just practical strategy direct from me.
          </p>
        </div>

        <div className="mb-10 max-w-4xl mx-auto space-y-4">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <input type="text" placeholder="Search your Northampton SEO question..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-[box-shadow,border-color]" />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
            {categoryFilters.map((cat, i) => <Button key={cat} data-index={i} variant={selectedCategory === cat ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(cat)} className="rounded-full text-xs transition-colors">
                {cat}
              </Button>)}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-start">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-4 lg:sticky lg:top-24 space-y-6">
            <div className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Sparkles className="size-24 text-primary" />
              </div>

              <div className="flex items-center gap-4 mb-6">
                <div className="relative shrink-0">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant in Northampton" loading="lazy" decoding="async" className="size-16 rounded-full object-cover border-2 border-primary" />
                  <span className="absolute bottom-0 right-0 size-3.5 bg-emerald-500 border-2 border-background rounded-full" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-foreground font-serif">
                    Yassine Essaadi
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Independent Freelance SEO Consultant
                  </p>
                </div>
              </div>

              <p className="text-sm text-foreground/90 leading-relaxed mb-6">
                "Have a specific question about your website or Northampton local rankings? I am always happy to analyze your site and give you honest, actionable advice."
              </p>

              <div className="space-y-3 mb-6 text-xs text-muted-foreground border-y border-border py-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Direct consultant access — no account managers</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Transparent local reporting &amp; clear KPIs</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Serving Northampton &amp; UK nationwide</span>
                </div>
              </div>

              <div className="space-y-3">
                <Button asChild className="w-full justify-center group font-medium" size="lg">
                  <Link to="/ContactPage">
                    Book Free SEO Consultation
                    <ArrowRight className="size-4 ml-1.5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button asChild variant="outline" className="w-full justify-center" size="sm">
                  <Link to="/LocalSEOPage">
                    Explore Local SEO Services
                  </Link>
                </Button>
              </div>

              <div className="mt-6 pt-4 border-t border-border/60 text-center space-y-1">
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-1.5">
                  <Mail className="size-3.5 text-primary" />
                  <a href="mailto:contact@ye-consultancy.com" className="hover:text-primary transition-colors">
                    contact@ye-consultancy.com
                  </a>
                </div>
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-1.5">
                  <Phone className="size-3.5 text-primary" />
                  <a href="tel:+447490984430" className="hover:text-primary transition-colors">
                    +44 7490984430
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-secondary/50 border border-border/80 rounded-xl p-5 flex items-start gap-3">
              <MessageSquare className="size-5 text-primary shrink-0 mt-0.5" />
              <div>
                <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-1">
                  Verified Local Growth
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Learn how my hands-on local SEO strategies generated sustainable Map Pack visibility and high-intent leads for UK businesses.
                </p>
                <Link to="/CaseStudiesPage" className="inline-flex items-center text-xs text-primary font-medium hover:underline mt-2">
                  View Case Studies <ArrowRight className="size-3 ml-1" />
                </Link>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-8">
            {filteredFaqs.length === 0 ? <div className="text-center py-12 bg-card rounded-2xl border border-border p-6">
                <HelpCircle className="size-10 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-semibold text-lg text-foreground mb-1">No matching questions found</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Try adjusting your search terms or select another category filter.
                </p>
                <Button variant="outline" size="sm" onClick={() => {
              setSelectedCategory("All Questions");
              setSearchQuery("");
            }}>
                  Reset Search Filters
                </Button>
              </div> : <Accordion type="single" collapsible className="w-full space-y-4">
                {filteredFaqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="bg-card border border-border rounded-xl px-5 py-1 shadow-2xs transition-colors hover:border-primary/40 data-[state=open]:border-primary/60 data-[state=open]:shadow-xs">
                    <AccordionTrigger className="py-4 hover:no-underline text-left transition-colors">
                      <div className="flex items-start gap-3 pr-2">
                        <span className="text-primary font-serif font-semibold text-base sm:text-lg leading-snug">
                          {faq.question}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5 border-t border-border/40 mt-1">
                      <p>{faq.answer}</p>
                      <div className="mt-4 flex items-center justify-between text-xs pt-3 border-t border-border/20">
                        <span className="bg-secondary text-secondary-foreground px-2.5 py-0.5 rounded-md font-medium">
                          {faq.category}
                        </span>
                        <Link to="/ContactPage" className="text-primary hover:underline inline-flex items-center font-medium">
                          Ask Yassine about this <ArrowRight className="size-3 ml-1" />
                        </Link>
                      </div>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>}
          </div>
        </div>

        <Separator className="my-12 md:my-16" />

        <div className="bg-card border border-border rounded-2xl p-6 md:p-10 text-center max-w-4xl mx-auto shadow-xs relative overflow-hidden">
          <div className="relative z-10 space-y-4">
            <h3 className="text-2xl sm:text-3xl font-serif font-bold text-foreground">
              Ready to grow your Northampton search traffic?
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base max-w-2xl mx-auto">
              Book a direct consultation with me. We will review your current rankings, pinpoint technical obstacles, and build a tailored local strategy.
            </p>
            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/ContactPage">
                  Book a Free Consultation
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/CaseStudiesPage">
                  Explore Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}